import React, { useState } from "react";
import { ListPlus, Trash2, Tag, X } from "lucide-react";
import { toast } from "react-toastify";
import { bulkContactAction } from "../../apis/contacts";

/**
 * BulkActionBar
 *
 * Sticky strip shown above the contacts table when at least one row is
 * selected. Triggers `/contacts/bulk` on the server. Exposes higher-level
 * UX (modal pickers) instead of `window.prompt`.
 *
 * Props:
 *   - phoneId       (string, required)
 *   - selectedIds   (number[])
 *   - lists         (ContactList[])
 *   - onClear       () => void
 *   - onMutated     () => void   - called after server work succeeds.
 */
const BulkActionBar = ({ phoneId, selectedIds, lists = [], onClear, onMutated }) => {
  const [picker, setPicker] = useState(null); // 'list' | 'tags' | null

  if (!selectedIds || selectedIds.length === 0) return null;

  const onDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} contact(s)? This cannot be undone.`)) return;
    try {
      await bulkContactAction({ phone_id: phoneId, action: "delete", ids: selectedIds });
      toast.success(`Deleted ${selectedIds.length} contact(s)`);
      onMutated && onMutated();
      onClear && onClear();
    } catch (err) {
      toast.error(err.response?.data?.error || "Bulk delete failed");
    }
  };

  return (
    <>
      <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-3 text-sm">
        <span className="text-amber-800 font-medium">
          {selectedIds.length} selected
        </span>
        <button
          onClick={() => setPicker("list")}
          className="inline-flex items-center gap-1 text-amber-900 hover:underline"
        >
          <ListPlus size={14} /> Add to list
        </button>
        <button
          onClick={() => setPicker("tags")}
          className="inline-flex items-center gap-1 text-amber-900 hover:underline"
        >
          <Tag size={14} /> Add tags
        </button>
        <button onClick={onDelete} className="inline-flex items-center gap-1 text-rose-700 hover:underline">
          <Trash2 size={14} /> Delete
        </button>
        <button onClick={onClear} className="ml-auto text-amber-900 hover:underline">
          Clear selection
        </button>
      </div>

      {picker === "list" && (
        <PickListModal
          phoneId={phoneId}
          lists={lists}
          selectedIds={selectedIds}
          onClose={() => setPicker(null)}
          onDone={() => { setPicker(null); onMutated && onMutated(); onClear && onClear(); }}
        />
      )}

      {picker === "tags" && (
        <AddTagsModal
          phoneId={phoneId}
          selectedIds={selectedIds}
          onClose={() => setPicker(null)}
          onDone={() => { setPicker(null); onMutated && onMutated(); onClear && onClear(); }}
        />
      )}
    </>
  );
};

const PickListModal = ({ phoneId, lists, selectedIds, onClose, onDone }) => {
  const [listId, setListId] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!listId) {
      toast.error("Choose a list");
      return;
    }
    setBusy(true);
    try {
      await bulkContactAction({
        phone_id: phoneId,
        action: "add_to_list",
        ids: selectedIds,
        payload: { list_id: parseInt(listId, 10) },
      });
      toast.success(`Added ${selectedIds.length} contact(s)`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || "Add to list failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title={`Add ${selectedIds.length} to list`} onClose={onClose}>
      <select
        value={listId}
        onChange={(e) => setListId(e.target.value)}
        className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 bg-white"
      >
        <option value="">Select a list…</option>
        {lists
          .filter((l) => l.type !== "dynamic")
          .map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
      <p className="text-xs text-gray-500 mt-2">
        Only static lists are shown. Dynamic lists are managed by rules.
      </p>
      <Footer onClose={onClose} onSubmit={submit} busy={busy} label="Add" />
    </Shell>
  );
};

const AddTagsModal = ({ phoneId, selectedIds, onClose, onDone }) => {
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const arr = tags.split(",").map((s) => s.trim()).filter(Boolean);
    if (arr.length === 0) {
      toast.error("Add at least one tag");
      return;
    }
    setBusy(true);
    try {
      await bulkContactAction({
        phone_id: phoneId,
        action: "add_tags",
        ids: selectedIds,
        payload: { tags: arr },
      });
      toast.success(`Tagged ${selectedIds.length} contact(s)`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || "Tagging failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title={`Tag ${selectedIds.length} contact(s)`} onClose={onClose}>
      <input
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        placeholder="e.g. priority, vip, demo-2024"
        className="w-full text-sm border border-gray-200 rounded-md px-3 py-2"
      />
      <p className="text-xs text-gray-500 mt-2">Separate with commas. Tags are merged into existing ones.</p>
      <Footer onClose={onClose} onSubmit={submit} busy={busy} label="Apply" />
    </Shell>
  );
};

const Shell = ({ title, onClose, children }) => (
  <>
    <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  </>
);

const Footer = ({ onClose, onSubmit, busy, label }) => (
  <div className="mt-4 flex justify-end gap-2">
    <button onClick={onClose} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">Cancel</button>
    <button
      onClick={onSubmit}
      disabled={busy}
      className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-60"
    >
      {busy ? "Working…" : label}
    </button>
  </div>
);

export default BulkActionBar;
